import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2, Table as TableIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiPost, apiGet, interactiveFetch } from '@/api';

interface DataPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    dataSourceId: string;
    objectName: string;
    query?: string;
    data?: any[];
    title?: string;
}

export const DataPreviewModal = ({ isOpen, onClose, dataSourceId, objectName, query, data = null, title = null }: DataPreviewModalProps) => {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            if (data) {
                setRows(data);
                setError(null);
                setLoading(false);
            } else if (dataSourceId && (objectName || query)) {
                fetchPreview();
            }
        } else {
            setRows([]);
            setError(null);
        }
    }, [isOpen, dataSourceId, objectName, query, data]);

    const fetchPreview = async () => {
        setLoading(true);
        setError(null);
        try {
            // First fetch the datasource to check its type
            const dsMeta = await apiGet(`/datasources/${dataSourceId}`);
            const isFileDatasource = [9, 10, 11, 12].includes(Number(dsMeta?.type));

            let responseData;
            if (isFileDatasource) {
                // Route to interactive runner for file datasources (Excel, CSV, etc.)
                responseData = await interactiveFetch('/datasource/file-preview', null, {
                    method: 'POST',
                    body: JSON.stringify({
                        dataSourceId: dataSourceId,
                        connectionString: dsMeta.connectionString,
                        configurationJson: dsMeta.configurationJson || {},
                        tableName: objectName,
                        query: query
                    })
                });
            } else {
                // Route to control plane for metadata/SQL execution
                responseData = await apiPost('/datasources/preview', null, {
                    dataSourceId: dataSourceId,
                    tableName: objectName,
                    query: query
                });
            }

            setRows(responseData || []);
        } catch (e: any) {
            setError(e.message || "Failed to load preview");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Determine columns dynamically from first row


    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-8">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-6xl h-[700px] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden"
            >
                {/* Header */}
                <div className="h-16 px-6 border-b border-gray-100 flex items-center justify-between bg-white">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                            <TableIcon size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">{title || 'Data Preview'}</h2>
                            <p className="text-xs text-gray-500">
                                {objectName} • First 50 rows
                            </p>
                        </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-gray-900">
                        <X size={20} />
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto bg-gray-50 p-6">
                    {loading ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400">
                            <Loader2 className="h-8 w-8 animate-spin mb-2 text-blue-500" />
                            <p className="text-sm">Loading Data...</p>
                        </div>
                    ) : error ? (
                        <div className="h-full flex items-center justify-center text-red-500 p-8 text-center">
                            <div>
                                <h3 className="font-semibold mb-1">Failed to load preview</h3>
                                <p className="text-sm opacity-80">{error}</p>
                            </div>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-gray-400">
                            No data found.
                        </div>
                    ) : (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm inline-block min-w-full align-middle">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        {Object.keys(rows[0] || {}).map(col => (
                                            <th key={col} className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs whitespace-nowrap">
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {rows.map((row, i) => (
                                        <tr key={i} className="hover:bg-blue-50/30 transition-colors">
                                            {Object.keys(row || {}).map(col => (
                                                <td key={col} className="px-4 py-2.5 text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px]" title={typeof row[col] === 'object' && row[col] !== null ? JSON.stringify(row[col]) : String(row[col] ?? '')}>
                                                    {(() => {
                                                        const val = row[col];
                                                        if (val === null || val === undefined) return <span className="text-gray-300 italic">null</span>;
                                                        if (typeof val === 'object') return JSON.stringify(val);
                                                        return String(val);
                                                    })()}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};
